import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary for bulk upload feature
 * Catches React errors and displays user-friendly message
 */
export class BulkUploadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to Sentry
    Sentry.captureException(error, {
      tags: { feature: 'bulk-deal-docs-upload' },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box p={3}>
          <Stack spacing={2} maxWidth={600}>
            <Alert severity='error'>
              <Typography variant='h6' gutterBottom>
                Something went wrong
              </Typography>
              <Typography variant='body2' gutterBottom>
                An error occurred while loading the bulk upload feature. This has been reported to our team.
              </Typography>
              {import.meta.env.DEV && this.state.error && (
                <Typography variant='caption' component='pre' sx={{ mt: 2, fontSize: '0.75rem' }}>
                  {this.state.error.message}
                </Typography>
              )}
            </Alert>
            <Box>
              <Button variant='outlined' onClick={this.handleReset} size='small'>
                Try Again
              </Button>
            </Box>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}
